import { useState } from 'react';
import { adminApi, schoolApi } from '../../api/endpoints.js';
import { useNotice } from '../../state/NoticeContext.jsx';
import {
  Button,
  Card,
  Muted,
  Select,
  Spinner,
  Tag,
  Text,
  useAsync
} from '../../ui.jsx';

const emptyForm = { name: '', rule: '', field: '', cutOff: '', cutOffFeePaying: '', note: '' };

export default function AdminProgrammes() {
  const notice = useNotice();
  const list = useAsync(() => adminApi.programmes(), []);
  const reference = useAsync(() => schoolApi.reference(), []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState('');

  if (list.loading || reference.loading) return <Spinner label="Loading your programmes" />;

  const programmes = list.data?.programmes || [];
  const fields = list.data?.fields || reference.data?.interests || {};
  const subjectNames = reference.data?.subjects || {};
  const awaiting = programmes.filter((p) => p.awaitingCutOff);

  const set = (patch) => setForm({ ...form, ...patch });

  const create = async (event) => {
    event.preventDefault();
    setBusy('create');
    try {
      await adminApi.createProgramme({
        name: form.name.trim(),
        rule: form.rule.trim(),
        field: form.field,
        // An empty box is a real value: it means no cut-off has been published.
        cutOff: form.cutOff === '' ? null : Number(form.cutOff),
        cutOffFeePaying: form.cutOffFeePaying === '' ? null : Number(form.cutOffFeePaying),
        note: form.note.trim() || undefined
      });
      notice.success('Programme added.');
      setForm(emptyForm);
      setAdding(false);
      list.reload();
    } catch (error) {
      notice.fromError(error);
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <h2>Programmes and cut-offs</h2>
      <Muted>
        You publish these. Entrance does not guess a cut-off: until you set one, students are told
        plainly that none has been published.
      </Muted>

      {awaiting.length ? (
        <div className="note">
          <b>{awaiting.length} {awaiting.length === 1 ? 'programme has' : 'programmes have'} no cut-off.</b>{' '}
          {awaiting.map((p) => p.name).join(', ')}. Students can still see these and apply, and
          Entrance will not rank them against a number you have not published.
        </div>
      ) : null}

      <Card>
        <div className="row sp">
          <b>{programmes.length} {programmes.length === 1 ? 'programme' : 'programmes'}</b>
          <Button size="sm" onClick={() => setAdding((a) => !a)}>
            {adding ? 'Cancel' : 'Add a programme'}
          </Button>
        </div>

        {adding ? (
          <form onSubmit={create} style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--br)' }}>
            <Text
              label="Programme name"
              required
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
            />
            <div style={{ height: 12 }} />
            <Text
              label="Subject requirement"
              required
              hint={`Write it the way you publish it, for example: C6 or better in English, plus three electives. Subjects: ${Object.values(subjectNames).slice(0, 6).join(', ')}, and so on.`}
              value={form.rule}
              onChange={(e) => set({ rule: e.target.value })}
            />
            <div style={{ height: 12 }} />
            <Select
              label="Field of study"
              required
              value={form.field}
              onChange={(e) => set({ field: e.target.value })}
            >
              <option value="">Choose a field</option>
              {Object.entries(fields).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Select>
            <div style={{ height: 12 }} />
            <div className="row">
              <div style={{ flex: 1 }}>
                <Text
                  label="Cut-off aggregate"
                  type="number"
                  min="0"
                  max="36"
                  hint="Leave empty if you have not published one."
                  value={form.cutOff}
                  onChange={(e) => set({ cutOff: e.target.value })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Text
                  label="Fee-paying cut-off"
                  type="number"
                  min="0"
                  max="36"
                  value={form.cutOffFeePaying}
                  onChange={(e) => set({ cutOffFeePaying: e.target.value })}
                />
              </div>
            </div>
            <div style={{ height: 12 }} />
            <Text
              label="Note for students"
              value={form.note}
              maxLength={500}
              onChange={(e) => set({ note: e.target.value })}
            />
            <div style={{ marginTop: 14 }}>
              <Button type="submit" busy={busy === 'create'}>Add programme</Button>
            </div>
          </form>
        ) : null}
      </Card>

      {programmes.length === 0 ? (
        <Card>
          <Muted>You have not published any programmes yet.</Muted>
        </Card>
      ) : (
        programmes.map((programme) => (
          <ProgrammeRow
            key={programme.id}
            programme={programme}
            fields={fields}
            onChanged={list.reload}
            onError={notice.fromError}
          />
        ))
      )}
    </>
  );
}

function ProgrammeRow({ programme, fields, onChanged, onError }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: programme.name,
    field: programme.field || '',
    cutOff: programme.cutOff ?? '',
    cutOffFeePaying: programme.cutOffFeePaying ?? '',
    note: programme.note || '',
    active: programme.active
  });
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm({ ...form, ...patch });

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.updateProgramme(programme.id, {
        name: form.name.trim(),
        field: form.field,
        // null clears a published number instead of leaving it stuck in place.
        cutOff: form.cutOff === '' ? null : Number(form.cutOff),
        cutOffFeePaying: form.cutOffFeePaying === '' ? null : Number(form.cutOffFeePaying),
        note: form.note,
        active: form.active
      });
      onChanged();
      setOpen(false);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="row sp" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <b>{programme.name}</b>
          <div className="mu">{programme.ruleText}</div>
          {programme.note ? <div className="mu">{programme.note}</div> : null}
          <div className="mu">
            {fields[programme.field] || programme.field}
            {programme.entranceExam ? ' · entrance exam' : ''}
            {programme.active ? '' : ' · hidden from students'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {programme.cutOff == null ? (
            <Tag tone="mid">No cut-off published</Tag>
          ) : (
            <Tag tone="ok">Cut-off {programme.cutOff}</Tag>
          )}
          {programme.cutOffFeePaying != null ? (
            <div className="mu" style={{ marginTop: 4 }}>Fee-paying {programme.cutOffFeePaying}</div>
          ) : null}
          <div style={{ marginTop: 8 }}>
            <Button size="sm" variant="s" onClick={() => setOpen((o) => !o)}>
              {open ? 'Close' : 'Edit'}
            </Button>
          </div>
        </div>
      </div>

      {open ? (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--br)' }}>
          <Text label="Programme name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
          <div style={{ height: 12 }} />
          <Select label="Field of study" value={form.field} onChange={(e) => set({ field: e.target.value })}>
            {Object.entries(fields).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </Select>
          <div style={{ height: 12 }} />
          <div className="row">
            <div style={{ flex: 1 }}>
              <Text
                label="Cut-off aggregate"
                type="number"
                min="0"
                max="36"
                hint="Empty clears it."
                value={form.cutOff}
                onChange={(e) => set({ cutOff: e.target.value })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Text
                label="Fee-paying cut-off"
                type="number"
                min="0"
                max="36"
                value={form.cutOffFeePaying}
                onChange={(e) => set({ cutOffFeePaying: e.target.value })}
              />
            </div>
          </div>
          <div style={{ height: 12 }} />
          <Text label="Note for students" value={form.note} onChange={(e) => set({ note: e.target.value })} />
          <div style={{ height: 12 }} />
          <label className="row sp" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => set({ active: e.target.checked })}
              style={{ width: 'auto', margin: 0 }}
            />
            <div>
              <b>Visible to students</b>
              <div className="mu">Turn this off to withdraw the programme without deleting its history.</div>
            </div>
          </label>
          <div style={{ marginTop: 14 }}>
            <Button busy={busy} onClick={save}>Save programme</Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
